import React from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { ContactInfo } from '@/types/company-config';

interface ContactInfoTabProps {
  data: ContactInfo;
  onChange: (field: string, value: unknown) => void;
  readOnly?: boolean;
}

const ContactInfoTab: React.FC<ContactInfoTabProps> = ({ data, onChange, readOnly = false }) => {
  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(field, event.target.value);
  };

  const handleAddressChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange('address', {
      ...data.address,
      [field]: event.target.value
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Contact Information
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Contact details for your company and support.
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Primary Email"
            value={data.email || ''}
            onChange={handleChange('email')}
            type="email"
            required
            disabled={readOnly}
            helperText="Main contact email for your company"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Support Email"
            value={data.supportEmail || ''}
            onChange={handleChange('supportEmail')}
            type="email"
            disabled={readOnly}
            helperText="Email for customer support inquiries"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Phone Number"
            value={data.phone || ''}
            onChange={handleChange('phone')}
            type="tel"
            placeholder="+1 (555) 123-4567"
            disabled={readOnly}
            helperText="Primary phone number for your company"
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Company Address
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Physical address for your company (optional).
          </Typography>
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Street Address"
            value={data.address?.street || ''}
            onChange={handleAddressChange('street')}
            disabled={readOnly}
            helperText="Street address including number and street name"
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="City"
            value={data.address?.city || ''}
            onChange={handleAddressChange('city')}
            disabled={readOnly}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="State/Province"
            value={data.address?.state || ''}
            onChange={handleAddressChange('state')}
            disabled={readOnly}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            fullWidth
            label="ZIP/Postal Code"
            value={data.address?.zipCode || ''}
            onChange={handleAddressChange('zipCode')}
            placeholder="12345 or 12345-6789"
            disabled={readOnly}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            fullWidth
            label="Country"
            value={data.address?.country || 'US'}
            onChange={handleAddressChange('country')}
            disabled={readOnly}
            helperText="Country code (e.g., US, CA, GB)"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactInfoTab;